// ─────────────────────────────────────────────────────────────────────────────
// AdminRefunds — top-level Refunds page (sibling of Transactions / Revenue).
// Created 28-May-2026 in response to user request to "make refund tab like
// revenue and transactions". Previously refunds lived only as a sub-tab
// inside AdminTransactions; promoting it surfaces the approval queue and the
// Auto / Semi-Auto / Manual mode switcher at the top of the admin's daily
// workflow — matching what payment gateways like Razorpay & Stripe do.
//
// Composition:
//   • Top: ModeBanner — global Auto/Semi-Auto/Manual switcher with copy
//     explaining what each mode does (calls /admin/refund-mode).
//   • Stat strip:    Pending approval · Credited · Failed · ₹ refunded · Avg %.
//   • Approval queue (status='requested') with one-click Approve.
//   • Full ledger of every refund with filters (status, raised-by, search,
//     date range) and a side drawer reused from AdminTransactions.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  MdRefresh, MdSearch, MdAssignmentReturn, MdCheckCircle, MdError,
  MdContentCopy, MdCheck, MdAutorenew,
  MdAccountBalanceWallet, MdReceiptLong, MdGavel, MdHourglassEmpty,
  MdInfoOutline,
} from 'react-icons/md';
import { adminAPI } from '../../services/api';
import { isSyntheticBackfill, settleRefundRow, displayTxnStatus } from './adminRefundUtils';

// ── Styling tokens (same palette as AdminTransactions) ──────────────────────
const CARD   = '#111827';
const BORDER = 'rgba(148,163,184,0.22)';
const SUBTLE = '#cbd5e1';
const MUTED  = '#94a3b8';
const TEXT   = '#f8fafc';
const ACCENT = '#f59e0b';
const GREEN  = '#10b981';
const RED    = '#ef4444';
const BLUE   = '#60a5fa';
const PURPLE = '#a78bfa';

const STATUS_LABEL = {
  requested: 'Awaiting approval', initiated: 'Initiated',
  processed: 'Refund processed', credited:  'Credited',
  historical: 'Historical reconciled', manual_credited: 'Manually credited',
  failed:    'Failed',
};
const STATUS_COLOR = {
  requested: ACCENT, initiated: ACCENT, processed: BLUE,
  credited:  GREEN, manual_credited: ACCENT, historical: MUTED,
  failed:    RED,
};
const RAISED_BY_COLOR = { patient: BLUE, hospital: RED, admin: PURPLE };
const RAISED_BY_LABEL = { patient: 'Patient', hospital: 'Hospital', admin: 'Admin' };

const POLICY_LABEL = {
  hospital_full:   'Hospital cancelled · 100%',
  admin:           'Admin override',
  patient_100:     'Patient · 100% (≥5h)',
  patient_75:      'Patient · 75% (≥4h)',
  patient_50:      'Patient · 50% (≥3h)',
  patient_25:      'Patient · 25% (≥2h)',
  patient_none:    'Patient · 0% (<2h)',
  patient_partial: 'Patient · 50% (legacy)',
  full: 'Hospital · 100% (legacy)', half: 'Patient · 50% (legacy)', none: 'No refund (legacy)',
};

const fmtRS = (n) => `₹${(Number(n)||0).toLocaleString('en-IN')}`;
function parseWhen(v) {
  if (v == null || v === '') return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}
function resolveWhen(row) {
  return parseWhen(row?.createdAt)
    || parseWhen(row?.updatedAt)
    || parseWhen(row?.creditedAt);
}
const fmtDT = (s) => {
  const d = parseWhen(s);
  return d ? d.toLocaleString('en-IN', {
    day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit', hour12:true,
  }) : '—';
};
const fmtRowWhen = (row) => fmtDT(resolveWhen(row));
const short = (s, n=14) => !s ? '—' : (String(s).length > n ? String(s).slice(0, n) + '…' : String(s));

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────
export default function AdminRefunds({ adminEmail, toast }) {
  const [rows, setRows]       = useState([]);
  const [totals, setTotals]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [approvingId, setApprovingId] = useState(null);

  const [toastMsg, setToastMsg] = useState(null);
  const notify = useCallback((msg, isError = false) => {
    if (toast) toast(msg, isError);
    else {
      setToastMsg({ msg, isError });
      window.clearTimeout(notify._t);
      notify._t = window.setTimeout(() => setToastMsg(null), 5000);
    }
  }, [toast]);

  // Filters
  const [statusF,   setStatusF]   = useState('');     // '', requested, initiated, processed, credited, failed
  const [raisedF,   setRaisedF]   = useState('');     // '', patient, hospital, admin
  const [search,    setSearch]    = useState('');
  const [from,      setFrom]      = useState('');
  const [to,        setTo]        = useState('');

  // View segment: 'queue' | 'ledger'
  // Default is 'queue', but if the queue is empty after load we auto-flip to
  // 'ledger' so the admin never lands on a blank screen when every refund has
  // already been resolved (the screenshot bug: 5 credited refunds but the page
  // showed "Queue is clear" with nothing else).
  const [seg, setSeg] = useState('queue');
  const [segUserPicked, setSegUserPicked] = useState(false);

  // Refund mode (global platform setting)
  const [mode, setMode]         = useState('auto');
  const [modeBusy, setModeBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const { data, error } = await adminAPI.listTransactions({
      type: 'refund', status: statusF, from, to, q: search, limit: 1000,
    });
    if (error) { setError(error); setLoading(false); return; }
    let list = data?.transactions || [];
    if (raisedF) {
      list = list.filter(r => String(r.cancelledBy || '').toLowerCase() === raisedF);
    }
    setRows(list);
    setTotals(data?.totals || null);
    setLoading(false);
  }, [statusF, raisedF, from, to, search]);

  useEffect(() => { load(); }, [load]);

  // Load current refund mode once
  useEffect(() => {
    (async () => {
      const { data } = await adminAPI.getRefundMode();
      if (data?.mode) setMode(String(data.mode).toLowerCase());
    })();
  }, []);

  const changeMode = async (newMode) => {
    if (newMode === mode) return;
    if (!window.confirm(
      `Switch refund handling to "${MODE_INFO[newMode].title}"?\n\n${MODE_INFO[newMode].help}`
    )) return;
    setModeBusy(true);
    const { error } = await adminAPI.setRefundMode(newMode);
    setModeBusy(false);
    if (error) { notify('Could not change refund mode: ' + error, true); return; }
    setMode(newMode);
    notify(`Refund mode set to ${MODE_INFO[newMode]?.title || newMode}`);
  };

  const approveRefund = async (r) => {
    const manual = isSyntheticBackfill(r);
    const confirmMsg = manual
      ? `Mark refund of ${fmtRS(r.amount)} as manually credited?\n\nHistorical reconciled row — no Razorpay API.`
      : `Approve refund of ${fmtRS(r.amount)} for ${r.userName || r.userPhone}?\n\nThis calls Razorpay to credit the customer.\nThis cannot be undone.`;
    if (!window.confirm(confirmMsg)) return;
    setApprovingId(r.transactionId);
    const { error } = await settleRefundRow(r, { adminEmail, adminAPI });
    setApprovingId(null);
    if (error) { notify('Could not approve refund: ' + error, true); return; }
    notify(manual ? '✅ Marked as manually credited' : '✅ Refund submitted to Razorpay');
    load();
  };

  // Derived rows for queue (status=requested)
  const queueRows  = useMemo(() => rows.filter(r => r.status === 'requested'),  [rows]);
  const ledgerRows = rows;

  // Auto-flip to Full ledger when the approval queue is empty but refunds
  // do exist — so admins always see something on load. Only fires until the
  // admin manually picks a segment, after which we respect their choice.
  useEffect(() => {
    if (loading || segUserPicked) return;
    if (seg === 'queue' && queueRows.length === 0 && ledgerRows.length > 0) {
      setSeg('ledger');
    }
  }, [loading, segUserPicked, seg, queueRows.length, ledgerRows.length]);

  const pickSeg = (s) => { setSegUserPicked(true); setSeg(s); };

  return (
    <div style={{padding:20}}>
      {toastMsg && (
        <div style={{
          position:'fixed', bottom:24, right:24, zIndex:9999, maxWidth:420,
          padding:'12px 16px', borderRadius:10, fontSize:13, fontWeight:600,
          color:'var(--text-primary)', boxShadow:'0 8px 32px rgba(0,0,0,0.45)',
          background: toastMsg.isError ? 'rgba(127,29,29,0.95)' : 'rgba(15,23,42,0.95)',
          border: `1px solid ${toastMsg.isError ? 'rgba(248,113,113,0.5)' : 'rgba(34,197,94,0.4)'}`,
        }} role="status">{toastMsg.msg}</div>
      )}
      {/* Page header */}
      <div style={{display:'flex', alignItems:'center', marginBottom:14, flexWrap:'wrap', gap:10}}>
        <div style={{flex:1, minWidth:240}}>
          <div style={{color:TEXT, fontSize:22, fontWeight:800, display:'flex', alignItems:'center', gap:10}}>
            <MdAssignmentReturn size={26} style={{color:BLUE}}/> Refunds
          </div>
          <div style={{color:SUBTLE, fontSize:12, marginTop:2}}>
            Approve, audit and configure how refunds are processed for every cancelled booking on HCEN.
          </div>
        </div>
        <button onClick={load} style={btnGhost}><MdRefresh/> Refresh</button>
      </div>

      {/* Mode banner */}
      <ModeBanner mode={mode} onChange={changeMode} busy={modeBusy}/>

      {/* Stat strip */}
      <StatStrip totals={totals}/>

      {/* Segment switcher: Queue | Ledger */}
      <div style={{
        marginTop:14, display:'flex', gap:8, flexWrap:'wrap',
        background:CARD, border:`1px solid ${BORDER}`, borderRadius:12, padding:8,
      }}>
        <SegBtn active={seg==='queue'}  onClick={()=>pickSeg('queue')}
          color={ACCENT}
          icon={<MdHourglassEmpty/>} label='Approval queue'
          count={queueRows.length} hint='Pending admin action'/>
        <SegBtn active={seg==='ledger'} onClick={()=>pickSeg('ledger')}
          color={BLUE}
          icon={<MdReceiptLong/>} label='Full ledger'
          count={totals?.byType?.refund ?? rows.length} hint='Every refund ever processed'/>
      </div>

      {/* Filters (only for ledger) */}
      {seg === 'ledger' && (
        <div style={{
          marginTop:12, display:'flex', flexWrap:'wrap', gap:10, alignItems:'center',
          background:CARD, border:`1px solid ${BORDER}`, borderRadius:12, padding:12,
        }}>
          <select value={statusF} onChange={e=>setStatusF(e.target.value)} style={inputBase}>
            <option value=''>All statuses</option>
            <option value='requested'>Awaiting approval</option>
            <option value='initiated'>Initiated</option>
            <option value='processed'>Refund processed</option>
            <option value='credited'>Credited (gateway)</option>
            <option value='historical'>Historical reconciled</option>
            <option value='failed'>Failed</option>
          </select>
          <select value={raisedF} onChange={e=>setRaisedF(e.target.value)} style={inputBase}>
            <option value=''>Anyone raised</option>
            <option value='patient'>Patient</option>
            <option value='hospital'>Hospital</option>
            <option value='admin'>Admin</option>
          </select>
          <input type='date' value={from} onChange={e=>setFrom(e.target.value)} style={inputBase}/>
          <input type='date' value={to}   onChange={e=>setTo(e.target.value)}   style={inputBase}/>
          <div style={{position:'relative', flex:'1 1 220px', minWidth:200}}>
            <MdSearch style={{position:'absolute', left:10, top:9, color:SUBTLE}}/>
            <input
              placeholder='Search refund ID, ARN, phone, booking, payment ID, name…'
              value={search}
              onChange={e=>setSearch(e.target.value)}
              style={{...inputBase, paddingLeft:32, width:'100%'}}
            />
          </div>
          {(statusF || raisedF || from || to || search) && (
            <button onClick={()=>{ setStatusF(''); setRaisedF(''); setFrom(''); setTo(''); setSearch(''); }}
              style={{...btnGhost, color:RED, borderColor:`${RED}55`}}>✕ Clear</button>
          )}
        </div>
      )}

      {/* Table */}
      <div style={{
        background:CARD, border:`1px solid ${BORDER}`, borderRadius:12,
        marginTop:14, overflow:'hidden',
      }}>
        {loading ? (
          <Empty msg='Loading refunds…'/>
        ) : error ? (
          <Empty msg={error} color={RED}/>
        ) : (seg === 'queue' ? queueRows : ledgerRows).length === 0 ? (
          <Empty
            icon={<MdAssignmentReturn size={48} style={{opacity:0.3}}/>}
            msg={seg === 'queue'
              ? '🎉 Queue is clear. Every raised refund has been resolved.'
              : 'No refunds match the filters.'}
          />
        ) : (
          <RefundTable
            rows={seg === 'queue' ? queueRows : ledgerRows}
            seg={seg}
            onApprove={approveRefund}
            approvingId={approvingId}
          />
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ModeBanner — Auto / Semi-Auto / Manual
// ─────────────────────────────────────────────────────────────────────────────
const MODE_INFO = {
  auto: {
    title:    'Automatic',
    color:    GREEN,
    icon:     <MdAutorenew/>,
    blurb:    'Refunds are credited the instant a booking is cancelled, with no admin review.',
    help:     'Best for low-fraud, high-trust environments. The system calls Razorpay immediately when a refund is raised.',
  },
  'semi-auto': {
    title:    'Semi-Automatic',
    color:    ACCENT,
    icon:     <MdGavel/>,
    blurb:    'Every refund waits in the approval queue until an admin clicks Approve.',
    help:     'Recommended default. Patients see "refund initiated" instantly in the app, but money only moves after you approve from this page.',
  },
  manual: {
    title:    'Manual',
    color:    BLUE,
    icon:     <MdAccountBalanceWallet/>,
    blurb:    'Nothing is sent to Razorpay automatically. Every refund must be initiated by hand.',
    help:     'Use this only for emergency lockdown — e.g. if Razorpay support is investigating a fraud incident on your account.',
  },
};
function ModeBanner({ mode, onChange, busy }) {
  const cur = MODE_INFO[mode] || MODE_INFO.auto;
  return (
    <div style={{
      background:`linear-gradient(135deg, ${cur.color}11, ${cur.color}06)`,
      border:`1px solid ${cur.color}55`,
      borderRadius:14, padding:16, marginBottom:14,
    }}>
      <div style={{display:'flex', alignItems:'center', gap:14, flexWrap:'wrap'}}>
        <div style={{
          width:48, height:48, borderRadius:12, background:`${cur.color}22`, color:cur.color,
          display:'grid', placeItems:'center', fontSize:26, flexShrink:0,
        }}>{cur.icon}</div>
        <div style={{flex:1, minWidth:200}}>
          <div style={{color:SUBTLE, fontSize:10.5, letterSpacing:0.8, fontWeight:800, textTransform:'uppercase'}}>
            Refund handling mode
          </div>
          <div style={{color:cur.color, fontSize:18, fontWeight:800, marginTop:2}}>{cur.title}</div>
          <div style={{color:TEXT, fontSize:12.5, marginTop:4, opacity:0.85}}>{cur.blurb}</div>
        </div>
        <div style={{display:'flex', gap:8, flexWrap:'wrap'}}>
          {Object.entries(MODE_INFO).map(([key, info]) => {
            const active = mode === key;
            return (
              <button key={key} disabled={busy} onClick={()=>onChange(key)} style={{
                padding:'8px 14px', borderRadius:10, fontSize:12.5, fontWeight:800,
                border:`1px solid ${active ? info.color : BORDER}`,
                background: active ? `${info.color}22` : 'transparent',
                color: active ? info.color : TEXT,
                cursor: busy ? 'wait' : 'pointer', fontFamily:'inherit',
                display:'inline-flex', alignItems:'center', gap:6, opacity: busy ? 0.6 : 1,
              }}>{info.icon}{info.title}</button>
            );
          })}
        </div>
      </div>
      <div style={{
        marginTop:10, display:'flex', gap:8, alignItems:'flex-start',
        color:SUBTLE, fontSize:11.5, lineHeight:1.5,
      }}>
        <MdInfoOutline size={14} style={{flexShrink:0, marginTop:2}}/>
        <div>{cur.help}</div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// StatStrip
// ─────────────────────────────────────────────────────────────────────────────
function StatStrip({ totals }) {
  const t = totals || {};
  const stats = [
    { label:'Pending approval',    val: t.refundsPending || 0,                     color:ACCENT, icon:<MdHourglassEmpty/> },
    { label:'Gateway credited',    val: t.refundsCredited || 0,                    color:GREEN,  icon:<MdCheckCircle/> },
    { label:'Historical',          val: t.refundsHistorical || 0,                  color:MUTED,  icon:<MdReceiptLong/> },
    { label:'Failed',              val: t.refundsFailed || 0,                      color:RED,    icon:<MdError/> },
    { label:'₹ Razorpay refunded', val: fmtRS(t.refundsCreditedAmount || 0),       color:GREEN,  icon:<MdAccountBalanceWallet/> },
    { label:'Total refunds',       val: t.byType?.refund || 0,                     color:BLUE,   icon:<MdReceiptLong/> },
  ];
  return (
    <div style={{display:'grid', gap:12, gridTemplateColumns:'repeat(auto-fit, minmax(170px, 1fr))'}}>
      {stats.map((s, i) => (
        <div key={i} style={{
          background:CARD, border:`1px solid ${BORDER}`, borderRadius:12,
          padding:14, display:'flex', alignItems:'center', gap:12,
        }}>
          <div style={{
            width:38, height:38, borderRadius:10,
            background:`${s.color}22`, color:s.color,
            display:'grid', placeItems:'center', fontSize:20,
          }}>{s.icon}</div>
          <div>
            <div style={{color:MUTED, fontSize:11, letterSpacing:0.4, textTransform:'uppercase', fontWeight:800}}>{s.label}</div>
            <div style={{color:s.color, fontSize:22, fontWeight:900, marginTop:3, lineHeight:1.1}}>{s.val}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Segment button
// ─────────────────────────────────────────────────────────────────────────────
function SegBtn({ active, onClick, icon, label, count, hint, color }) {
  return (
    <button onClick={onClick} style={{
      flex:'1 1 220px', padding:'12px 14px', borderRadius:10, border:'none', cursor:'pointer',
      background: active ? `${color}22` : 'transparent',
      color:      active ? color : TEXT,
      fontWeight: active ? 800 : 600, fontFamily:'inherit', fontSize:13,
      display:'flex', alignItems:'center', gap:10, textAlign:'left',
    }}>
      <div style={{
        width:32, height:32, borderRadius:8,
        background: active ? `${color}33` : 'rgba(148,163,184,0.12)',
        color: active ? color : SUBTLE,
        display:'grid', placeItems:'center', fontSize:18, flexShrink:0,
      }}>{icon}</div>
      <div style={{flex:1, minWidth:0}}>
        <div style={{display:'flex', alignItems:'center', gap:6}}>
          <span>{label}</span>
          <span style={{
            background: active ? `${color}33` : 'rgba(148,163,184,0.15)',
            color: active ? color : SUBTLE,
            padding:'1px 8px', borderRadius:999, fontSize:11, fontWeight:800,
          }}>{count}</span>
        </div>
        <div style={{color:SUBTLE, fontSize:10.5, fontWeight:500, marginTop:1}}>{hint}</div>
      </div>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Refund table
// ─────────────────────────────────────────────────────────────────────────────
function RefundTable({ rows, seg, onApprove, approvingId }) {
  const headers = seg === 'queue'
    ? ['Created', 'Raised by', 'User', 'Hospital', 'Amount', '%', 'Reason', 'Action']
    : ['Created', 'Raised by', 'User', 'Hospital', 'Amount', '%', 'Status', 'Refund Ref', 'Action'];
  return (
    <div style={{maxHeight:'62vh', overflow:'auto'}}>
      <table style={{width:'100%', borderCollapse:'collapse', fontSize:14, color:TEXT}}>
        <thead style={{position:'sticky', top:0, background:'#0f172a', zIndex:1, boxShadow:'0 1px 0 rgba(148,163,184,0.18)'}}>
          <tr>{headers.map(h => <th key={h} style={th}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <RefundRow key={r.transactionId} r={r} seg={seg} busy={approvingId === r.transactionId} onApprove={() => onApprove(r)}/>
          ))}
        </tbody>
      </table>
    </div>
  );
}
function RefundRow({ r, seg, onApprove, busy }) {
  const raisedBy      = String(r.cancelledBy || '').toLowerCase();
  const raisedByColor = RAISED_BY_COLOR[raisedBy] || SUBTLE;
  const raisedByLabel = RAISED_BY_LABEL[raisedBy] || (r.cancelledBy || '—');
  const dispStatus    = displayTxnStatus(r);
  const statusColor   = STATUS_COLOR[dispStatus] || STATUS_COLOR[r.status] || SUBTLE;
  const ref           = r.gatewayRefundId || r.gatewayPaymentId || r.transactionId;
  const synthetic     = isSyntheticBackfill(r);
  const approveLabel  = synthetic ? 'Mark manual credit' : 'Approve via Razorpay';
  const hover = (e, on) => { e.currentTarget.style.background = on ? 'rgba(148,163,184,0.06)' : 'transparent'; };

  return (
    <tr style={{borderTop:`1px solid ${BORDER}`}}
        onMouseEnter={e=>hover(e,true)} onMouseLeave={e=>hover(e,false)}>
      <td style={td}>{fmtRowWhen(r)}</td>
      <td style={td}>
        <span style={pill(raisedByColor)} title={POLICY_LABEL[r.refundPolicy] || ''}>{raisedByLabel}</span>
      </td>
      <td style={td}>
        <div style={{fontWeight:900, color:TEXT}}>{r.userName || '—'}</div>
        <div style={{color:SUBTLE, fontSize:12, fontWeight:700}}>{r.userPhone || ''}</div>
        {synthetic && (
          <span style={{...pill(ACCENT), marginTop:4, fontSize:9}} title='Reconciled from Bookings — mark manual credit only'>
            Historical
          </span>
        )}
      </td>
      <td style={{...td, maxWidth:160}}>
        <div style={{...ellipsis, fontWeight:800, color:TEXT}}>{r.hospitalName || r.hospitalId || '—'}</div>
      </td>
      <td style={{...td, fontWeight:900, color:GREEN, fontSize:14}}>+{fmtRS(r.amount || 0)}</td>
      <td style={{...td, fontWeight:800, color:TEXT}}>{r.refundPercent ? `${r.refundPercent}%` : '—'}</td>

      {seg === 'queue' ? (
        <>
          <td style={{...td, color:SUBTLE, maxWidth:220}}>
            <div style={ellipsis} title={r.refundReason || ''}>
              {r.refundReason || POLICY_LABEL[r.refundPolicy] || '—'}
            </div>
          </td>
          <td style={td}>
            <button disabled={busy} onClick={onApprove} style={{
              padding:'6px 14px', borderRadius:8,
              border:`1px solid ${synthetic ? ACCENT : GREEN}`,
              background: synthetic ? `${ACCENT}22` : `${GREEN}22`,
              color: synthetic ? ACCENT : GREEN,
              fontWeight:800, fontSize:12, fontFamily:'inherit',
              cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.6 : 1,
              display:'inline-flex', alignItems:'center', gap:6,
            }}><MdCheck size={14}/> {busy ? 'Working…' : approveLabel}</button>
          </td>
        </>
      ) : (
        <>
          <td style={td}>
            <span style={pill(statusColor)}>{STATUS_LABEL[dispStatus] || dispStatus}</span>
            {r.razorpayStatus && !synthetic && (
              <div style={{ fontSize:10, color:MUTED, marginTop:3 }}>Razorpay: {r.razorpayStatus}</div>
            )}
          </td>
          <td style={{...td, fontFamily:'monospace', fontSize:11}}>
            <CopyInline value={ref} label={short(ref, 14)}/>
          </td>
          <td style={td}>
            {r.status === 'requested' ? (
              <button disabled={busy} onClick={onApprove} style={{
                padding:'4px 10px', borderRadius:6,
                border:`1px solid ${synthetic ? ACCENT : GREEN}`,
                background: synthetic ? `${ACCENT}22` : `${GREEN}22`,
                color: synthetic ? ACCENT : GREEN,
                fontWeight:800, fontSize:11, fontFamily:'inherit',
                cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.6 : 1,
                display:'inline-flex', alignItems:'center', gap:4,
              }}><MdCheck size={13}/> {busy ? '…' : (synthetic ? 'Manual' : 'Approve')}</button>
            ) : <span style={{color:SUBTLE, fontSize:11}}>—</span>}
          </td>
        </>
      )}
    </tr>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
function CopyInline({ value, label }) {
  const [copied, setCopied] = useState(false);
  if (!value) return <span style={{color:SUBTLE}}>—</span>;
  return (
    <span title={value} style={{display:'inline-flex', alignItems:'center', gap:4, color:TEXT}}>
      {label}
      <MdContentCopy
        size={12}
        style={{cursor:'pointer', color: copied ? GREEN : SUBTLE}}
        onClick={(e) => {
          e.stopPropagation();
          navigator.clipboard?.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1200);
        }}
      />
    </span>
  );
}

function Empty({ msg, icon, color }) {
  return (
    <div style={{padding:50, textAlign:'center', color: color || SUBTLE}}>
      {icon}
      <div style={{marginTop:10, fontSize:13}}>{msg}</div>
    </div>
  );
}

const pill = (c) => ({
  padding:'2px 8px', borderRadius:999, fontSize:10.5, fontWeight:800,
  background:`${c}22`, color:c, display:'inline-block',
});
const ellipsis = { whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' };
const inputBase = {
  background:'#0f172a', border:`1px solid ${BORDER}`,
  borderRadius:8, padding:'7px 10px', color:TEXT, fontSize:13, fontWeight:700, fontFamily:'inherit',
  outline:'none',
};
const btnGhost = {
  background:'transparent', border:`1px solid ${BORDER}`,
  color:TEXT, padding:'7px 12px', borderRadius:8, fontSize:12, fontWeight:700,
  cursor:'pointer', fontFamily:'inherit',
  display:'inline-flex', alignItems:'center', gap:6,
};
const th = {
  textAlign:'left', padding:'12px 14px', color:MUTED,
  fontSize:11.5, fontWeight:900, letterSpacing:0.6, textTransform:'uppercase',
  borderBottom:`1px solid ${BORDER}`, whiteSpace:'nowrap',
};
const td = { padding:'12px 14px', color:TEXT, fontSize:14, verticalAlign:'middle', whiteSpace:'nowrap' };
